import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import CopyRichButton from '../../components/ui/CopyRichButton.jsx';
import { fmtDate } from '../../utils/format.js';
import { LEARN } from '../events/StampsSection.jsx';

// Quick Stamp: the stamp maker as an installable app (a PWA scoped to /stamp/). It uses the
// same stamp API as the event page; the manifest, icons and service worker live in
// public/stamp/. Recent stamps are kept on the device so they can still be copied offline.

const DURATIONS = [['14', '2 weeks'], ['60', '60 days'], ['180', '6 months']];
const EVENT_KEY = (uid) => `ss_qs_event_${uid}`;
const RECENT_KEY = (uid, eventId) => `ss_qs_recent_${uid}_${eventId}`;
const INSTALL_HIDDEN = 'ss_qs_install_hidden';

const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isMacSafari = () => /Macintosh/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|Edg|Firefox/.test(navigator.userAgent);
const read = (key, fallback) => { try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch { return fallback; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode */ } };

// The page's <head> becomes the app's while it's open: manifest, icon, title, colour.
function useAppHead() {
  useEffect(() => {
    const added = [];
    const add = (tag, attrs) => { const el = document.createElement(tag); Object.assign(el, attrs); document.head.appendChild(el); added.push(el); };
    add('link', { rel: 'manifest', href: '/stamp/manifest.webmanifest' });
    add('link', { rel: 'apple-touch-icon', href: '/stamp/apple-touch-icon.png' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: 'Quick Stamp' });
    // The app's scope is /stamp/, so keep the address inside it (the router treats /stamp the same)
    if (window.location.pathname === '/stamp') window.history.replaceState(window.history.state, '', '/stamp/');
    const title = document.title;
    document.title = 'Quick Stamp | SummitShield';
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/stamp/sw.js', { scope: '/stamp/' }).catch(() => {});
    return () => { added.forEach((el) => el.remove()); document.title = title; };
  }, []);
}

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

function ShieldMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" /><path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function InstallCard({ prompt, onInstall, onHide }) {
  let how;
  if (prompt) how = null;
  else if (isIos()) how = <>Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</>;
  else if (isMacSafari()) how = <>In Safari’s menu, choose <strong>File</strong>, then <strong>Add to Dock</strong>.</>;
  else how = <>Use the install icon at the right of your browser’s address bar, or <strong>Install app</strong> in its menu. Chrome and Edge both do this.</>;
  return (
    <section className="qs-card qs-install" aria-labelledby="qs-install-h">
      <div className="qs-install-icon"><ShieldMark size={22} /></div>
      <div className="qs-install-text">
        <h2 id="qs-install-h">Keep Quick Stamp handy</h2>
        <p>Install it and it opens in its own window, from your Start menu, Applications folder or home screen. No download needed.</p>
        {how && <p className="qs-install-how">{how}</p>}
      </div>
      <div className="qs-install-actions">
        {prompt && <button type="button" className="btn btn-primary btn-small" onClick={onInstall}>Install</button>}
        <button type="button" className="btn btn-secondary btn-small" onClick={onHide}>Not now</button>
      </div>
    </section>
  );
}

function MakeForm({ events, eventId, setEventId, online, onMade }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState('60');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const make = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { stamp } = await api(`/portal/events/${eventId}/stamps`, { method: 'POST', body: { label: label.trim() || null, expires_in_days: Number(days) } });
      setLabel(''); onMade(stamp);
    } catch (err) {
      setError(err.data?.fields?.expires_in_days || err.message || 'We couldn’t make a stamp. Please try again.');
    } finally { setBusy(false); }
  };
  return (
    <form className="qs-card qs-make" onSubmit={make} aria-labelledby="qs-make-h">
      <div>
        <h1 id="qs-make-h">Make a stamp</h1>
        <p className="qs-muted">One stamp per email. It shows people the email really came from you.</p>
      </div>
      {events.length > 1 && (
        <div className="qs-field">
          <label htmlFor="qs-event">Event</label>
          <select id="qs-event" value={eventId} onChange={(e) => setEventId(Number(e.target.value))}>
            {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>
      )}
      <div className="qs-field">
        <label htmlFor="qs-label">What’s it for? <span>(optional)</span></label>
        <input id="qs-label" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} placeholder="Stand invoices, 4 Nov" />
        <p className="qs-hint">Shown on the stamp’s page, so people know which email it belongs to.</p>
      </div>
      <fieldset className="qs-field">
        <legend>Works for</legend>
        <div className="qs-seg">
          {DURATIONS.map(([v, l]) => (
            <button key={v} type="button" aria-pressed={days === v} className={days === v ? 'is-on' : ''} onClick={() => setDays(v)}>{l}</button>
          ))}
        </div>
      </fieldset>
      {error && <p className="qs-error" role="alert">{error}</p>}
      <button type="submit" className="btn btn-primary qs-go" disabled={busy || !online || !eventId}>
        {busy ? 'Making…' : online ? 'Make stamp' : 'You’re offline'}
      </button>
    </form>
  );
}

function ReadyView({ stamp, eventName, fresh, onBack }) {
  const canShare = typeof navigator.share === 'function';
  const share = () => navigator.share({ title: `Stamp ${stamp.code}`, text: `Verify this email: ${stamp.url}`, url: stamp.url }).catch(() => {});
  return (
    <div className="qs-stack">
      <section className="qs-card" aria-labelledby="qs-ready-h">
        <p className="qs-kicker">{fresh ? 'Your stamp is ready' : 'Stamp'}</p>
        <h1 id="qs-ready-h" className="qs-code">{stamp.code}</h1>
        <p className="qs-muted">{stamp.label ? `${stamp.label} · ` : ''}{eventName} · works until {fmtDate(stamp.expires_at)}</p>
        <div className="qs-badge"><img src={stamp.badge_png_url} alt="The stamp’s badge, as it will look in your email" /></div>
        <ol className="qs-steps">
          <li>
            <strong>Paste it into your email</strong>
            <span className="qs-muted">At the bottom, above or below your signature.</span>
            <div className="qs-row">
              <CopyRichButton primary label="Copy for your email" html={stamp.html} text={`Verify this email: ${stamp.url}`} />
              <CopyButton text={stamp.html} label="Copy badge code" />
              {canShare && <button type="button" className="btn btn-small btn-secondary" onClick={share}>Share</button>}
            </div>
          </li>
          <li>
            <strong>Send it with us in Cc</strong>
            <span className="qs-muted">Add <span className="mono">{LEARN}</span> to Cc or Bcc. That’s how the stamp learns what your real email looks like.</span>
            <div className="qs-row"><CopyButton text={LEARN} label="Copy address" /></div>
          </li>
        </ol>
      </section>
      <button type="button" className="btn btn-secondary qs-back" onClick={onBack}>Make another stamp</button>
    </div>
  );
}

function RecentList({ stamps, onOpen }) {
  if (!stamps.length) return null;
  return (
    <section className="qs-recent" aria-labelledby="qs-recent-h">
      <h2 id="qs-recent-h">Recent stamps</h2>
      <ul>
        {stamps.map((s) => (
          <li key={s.id}>
            <button type="button" onClick={() => onOpen(s)} className="qs-recent-open">
              <strong>{s.label || s.code}</strong>
              <span>{s.label ? `${s.code} · ` : ''}until {fmtDate(s.expires_at)}</span>
            </button>
            <CopyRichButton label="Copy" html={s.html} text={`Verify this email: ${s.url}`} />
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---- Report a scam: bring one in from the desktop mail client or phone in a few seconds ----

const MAX_FILE = 15 * 1024 * 1024;
const REPORT_KINDS = [['file', 'Email file'], ['paste', 'Paste it'], ['approach', 'Call or text']];
const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error('That file couldn’t be read.'));
  r.readAsDataURL(file);
});

function ReportForm({ organisation, events, online, initial, onDone }) {
  const [kind, setKind] = useState(initial?.file ? 'file' : initial?.text ? 'paste' : 'file');
  const [file, setFile] = useState(initial?.file || null);
  const [text, setText] = useState(initial?.text || '');
  const [numbers, setNumbers] = useState('');
  const [details, setDetails] = useState('');
  const [channel, setChannel] = useState('phone');
  const [eventId, setEventId] = useState('');
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (initial?.file) { setFile(initial.file); setKind('file'); } else if (initial?.text) { setText(initial.text); setKind('paste'); } }, [initial]);
  const mine = events.filter((e) => Number(e.organisation_id) === Number(organisation?.id));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    let body;
    if (kind === 'file') {
      if (!file) return setError('Choose the email file, or drag it here.');
      if (file.size > MAX_FILE) return setError('That file is too big (15 MB at most).');
      body = { type: 'file', filename: file.name, content_base64: await toBase64(file) };
    } else if (kind === 'paste') {
      if (text.trim().length < 20) return setError('Paste the whole email: its text, or better, its source.');
      body = { type: 'paste', text };
    } else {
      if (!numbers.trim() && details.trim().length < 10) return setError('Give the number they used, or say what happened.');
      body = { type: 'approach', approach: { channel, numbers, details } };
    }
    if (eventId) body.event_id = Number(eventId);
    setBusy(true);
    try {
      const res = await api(`/portal/organisations/${organisation.id}/inbox/add`, { method: 'POST', body });
      setFile(null); setText(''); setNumbers(''); setDetails('');
      onDone(res);
    } catch (err) {
      const f = err.data?.fields; setError(f ? Object.values(f)[0] : err.message);
    } finally { setBusy(false); }
  };

  return (
    <form className="qs-card" onSubmit={submit} aria-labelledby="qs-report-h" noValidate>
      <div>
        <h1 id="qs-report-h">Report a scam</h1>
        <p className="qs-muted">Got something suspicious in your own inbox, or a strange call? Send it to SummitShield. We check it straight away, and you decide what it is in Messages.</p>
      </div>
      <div className="qs-seg" role="tablist" aria-label="What are you reporting?">
        {REPORT_KINDS.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={kind === k} className={kind === k ? 'is-on' : ''} onClick={() => { setKind(k); setError(''); }}>{l}</button>)}
      </div>
      {kind === 'file' && (
        <div className="qs-field">
          <label htmlFor="qs-file">The email</label>
          <div className={`qs-drop${over ? ' is-over' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); if (e.dataTransfer.files?.[0]) { setFile(e.dataTransfer.files[0]); setError(''); } }}>
            <input id="qs-file" type="file" accept=".eml,.msg,.mbox,.txt,message/rfc822,application/vnd.ms-outlook" onChange={(e) => { setFile(e.target.files?.[0] || null); setError(''); }} />
            <p>{file ? <><strong>{file.name}</strong> · {Math.ceil(file.size / 1024)} KB</> : <>Drag the email here from Outlook, Apple Mail or Thunderbird, or <span className="qs-link">choose a file</span></>}</p>
          </div>
          <p className="qs-hint">.eml or .msg, up to 15 MB. Using Gmail? Open the email, choose ⋮ then Download message.</p>
        </div>
      )}
      {kind === 'paste' && (
        <div className="qs-field">
          <label htmlFor="qs-paste">The email or message</label>
          <textarea id="qs-paste" rows={8} value={text} onChange={(e) => { setText(e.target.value); setError(''); }} />
          <p className="qs-hint">Its source is best (Gmail: ⋮ then Show original). Otherwise its text, with the From and Subject lines.</p>
        </div>
      )}
      {kind === 'approach' && (
        <>
          <div className="qs-field">
            <label htmlFor="qs-channel">How did they get in touch?</label>
            <select id="qs-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
              {[['phone', 'Phone call'], ['sms', 'Text message'], ['whatsapp', 'WhatsApp'], ['social', 'Social media message'], ['other', 'Another way']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="qs-field">
            <label htmlFor="qs-numbers">The number they used <span>(optional)</span></label>
            <input id="qs-numbers" value={numbers} onChange={(e) => setNumbers(e.target.value)} inputMode="tel" placeholder="+44 20 7946 0000" />
          </div>
          <div className="qs-field">
            <label htmlFor="qs-details">What happened</label>
            <textarea id="qs-details" rows={5} maxLength={5000} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Said they were our official AV supplier and wanted a deposit by card." />
          </div>
        </>
      )}
      {mine.length > 0 && (
        <div className="qs-field">
          <label htmlFor="qs-revent">Which event is it about? <span>(optional)</span></label>
          <select id="qs-revent" value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Not one event in particular</option>
            {mine.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          </select>
        </div>
      )}
      {error && <p className="qs-error" role="alert">{error}</p>}
      <button type="submit" className="btn btn-primary qs-go" disabled={busy || !online}>{busy ? 'Sending…' : online ? 'Send it to SummitShield' : 'You’re offline'}</button>
    </form>
  );
}

function ReportDone({ result, onAnother }) {
  return (
    <section className="qs-card qs-done" role="status">
      <span className="qs-done-icon" aria-hidden="true">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l4 4 10-10" /></svg>
      </span>
      <h1>{result.existing ? 'You’d already sent us this one' : 'Thanks, we’ve got it'}</h1>
      <p className="qs-muted">We’re checking it now, which takes a few seconds. Once you confirm it’s a scam, people checking your event’s page are warned about it.</p>
      <div className="qs-row qs-row-center">
        <a className="btn btn-primary btn-small" href={`/inbox/${result.id}`} target="_blank" rel="noreferrer">See it in Messages</a>
        <button type="button" className="btn btn-secondary btn-small" onClick={onAnother}>Report another</button>
      </div>
    </section>
  );
}

export default function QuickStamp() {
  const { user, organisation, logout } = useAuth();
  useAppHead();
  const online = useOnline();
  const uid = user?.id;
  const [events, setEvents] = useState(null);
  const [eventId, setEventIdState] = useState(() => read(EVENT_KEY(uid), null));
  const [recent, setRecent] = useState([]);
  const [open, setOpen] = useState(null);        // { stamp, fresh }
  const [prompt, setPrompt] = useState(null);
  const [installHidden, setInstallHidden] = useState(() => isStandalone() || read(INSTALL_HIDDEN, false));
  const [error, setError] = useState('');
  const [mode, setMode] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return q.has('report') || q.has('text') || q.has('url') || q.has('title') ? 'report' : 'stamp';
  });
  const [shared, setShared] = useState(() => {
    // Shared to the app from a phone's Share menu (manifest share_target)
    const q = new URLSearchParams(window.location.search);
    const text = [q.get('title'), q.get('text'), q.get('url')].filter(Boolean).join('\n');
    return text ? { text } : null;
  });
  const [reported, setReported] = useState(null);

  // Opened from an email file ("Open with Quick Stamp", manifest file_handlers)
  useEffect(() => {
    if (!('launchQueue' in window)) return;
    window.launchQueue.setConsumer(async (params) => {
      const handle = params.files?.[0];
      if (!handle) return;
      const file = await handle.getFile();
      setMode('report'); setReported(null); setShared({ file });
    });
  }, []);
  useEffect(() => {
    if (window.location.search) window.history.replaceState(window.history.state, '', '/stamp/');
  }, []);

  useEffect(() => {
    const catchPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const installed = () => { setPrompt(null); setInstallHidden(true); };
    window.addEventListener('beforeinstallprompt', catchPrompt);
    window.addEventListener('appinstalled', installed);
    return () => { window.removeEventListener('beforeinstallprompt', catchPrompt); window.removeEventListener('appinstalled', installed); };
  }, []);

  useEffect(() => {
    api('/portal/events').then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const list = (r.events || []).filter((e) => e.status !== 'archived');
      // Upcoming and running events first, then past ones
      list.sort((a, b) => Number(!!a.ends_on && a.ends_on < today) - Number(!!b.ends_on && b.ends_on < today));
      setEvents(list); write(`ss_qs_events_${uid}`, list);
      setEventIdState((cur) => (list.some((e) => e.id === cur) ? cur : list[0]?.id || null));
    }).catch((err) => {
      const cached = read(`ss_qs_events_${uid}`, null);
      if (cached) setEvents(cached); else setError(err.message);
    });
  }, [uid]);

  const setEventId = (id) => { setEventIdState(id); write(EVENT_KEY(uid), id); setOpen(null); };

  const loadRecent = useCallback(() => {
    if (!eventId) return;
    setRecent(read(RECENT_KEY(uid, eventId), []));
    api(`/portal/events/${eventId}/stamps`).then((r) => {
      const usable = (r.stamps || []).filter((s) => ['unbound', 'bound'].includes(s.state)).slice(0, 5);
      setRecent(usable); write(RECENT_KEY(uid, eventId), usable);
    }).catch(() => { /* offline: keep the copy on this device */ });
  }, [uid, eventId]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  const event = useMemo(() => (events || []).find((e) => e.id === eventId), [events, eventId]);
  const install = async () => { if (!prompt) return; prompt.prompt(); await prompt.userChoice.catch(() => {}); setPrompt(null); };
  const hideInstall = () => { setInstallHidden(true); write(INSTALL_HIDDEN, true); };

  if (organisation && !organisation.onboarded) return <Navigate to="/onboarding" replace />;

  return (
    <div className="qs-shell">
      <header className="qs-top">
        <span className="qs-mark"><ShieldMark /></span>
        <div className="qs-top-text">
          <strong>Quick Stamp</strong>
          <span>{organisation?.name || 'SummitShield'}</span>
        </div>
        <details className="qs-menu">
          <summary aria-label="Account">{(user?.first_name || user?.email || '?').slice(0, 1).toUpperCase()}</summary>
          <div className="qs-menu-pop">
            <p>{user?.email}</p>
            <a href="/dashboard" target="_blank" rel="noreferrer">Open the portal</a>
            <a href="/help/home" target="_blank" rel="noreferrer">Help</a>
            <button type="button" onClick={async () => { await logout(); window.location.href = '/login'; }}>Sign out</button>
          </div>
        </details>
      </header>

      <main className="qs-main">
        {!online && <p className="qs-offline" role="status">You’re offline. You can still copy your recent stamps.</p>}
        {!installHidden && <InstallCard prompt={prompt} onInstall={install} onHide={hideInstall} />}
        {error && <p className="qs-error" role="alert">{error}</p>}
        {events && !events.length && (
          <section className="qs-card">
            <h1>No events yet</h1>
            <p className="qs-muted">Stamps belong to an event. Add your event in the portal first.</p>
            <a className="btn btn-primary" href="/events" target="_blank" rel="noreferrer">Open your events</a>
          </section>
        )}
        {events?.length > 0 && (
          <div className="qs-modes" role="tablist" aria-label="What do you want to do?">
            <button type="button" role="tab" aria-selected={mode === 'stamp'} className={mode === 'stamp' ? 'is-on' : ''} onClick={() => setMode('stamp')}>Make a stamp</button>
            <button type="button" role="tab" aria-selected={mode === 'report'} className={mode === 'report' ? 'is-on' : ''} onClick={() => setMode('report')}>Report a scam</button>
          </div>
        )}
        {events?.length > 0 && mode === 'report' && (reported
          ? <ReportDone result={reported} onAnother={() => { setReported(null); setShared(null); }} />
          : <ReportForm organisation={organisation} events={events} online={online} initial={shared} onDone={setReported} />)}
        {events?.length > 0 && mode === 'stamp' && (open
          ? <ReadyView stamp={open.stamp} fresh={open.fresh} eventName={event?.name || ''} onBack={() => setOpen(null)} />
          : <MakeForm events={events} eventId={eventId} setEventId={setEventId} online={online} onMade={(s) => { setOpen({ stamp: s, fresh: true }); loadRecent(); }} />)}
        {events?.length > 0 && mode === 'stamp' && !open && <RecentList stamps={recent} onOpen={(s) => setOpen({ stamp: s, fresh: false })} />}
      </main>
    </div>
  );
}
